/**
 * Game Ui Splitter 面板 - 拆分会话快照（断点续传用）
 *
 * 把活跃拆分会话（clientId / 源图 / 阶段）持久化到 localStorage，刷新后据此恢复任务。
 * QA 0911 拍板多槽化：并行拆分（会话池）下每个在途会话各留一条快照，后发起的任务
 * 不再覆盖先任务的恢复凭证（单槽时代先任务刷新后彻底隐身）。编码期会话 clientId 尚未
 * 生成，用合成键暂存，prepare 拿到真实 clientId 后由 upsert 规则替换同源图的空键条目。
 *
 * 存储形状：{ v: 1, sessions: { [clientId]: Snapshot } }；兼容迁移旧单槽对象。
 */
import { ref, watch } from 'vue';

export type SplitSessionPhase = 'encoding' | 'prepared' | 'splitting';

export interface SplitSessionSnapshot {
  clientId: string;
  taskId: string;
  sourceImageUrl: string;
  phase: SplitSessionPhase;
  /** 流程类型：拆分或提取（恢复时透传 handleSplitDone 的 isExtract） */
  flow?: 'split' | 'extract';
  /** 精确入口：恢复弹窗按原模式打开（自动/手动/提取），优先级高于 flow 派生的 manual */
  entry?: 'auto-split' | 'manual-split' | 'extract';
  /** 快照所属作品 ID：切画布/新画布时据此跳过误恢复，避免「检测到未完成的拆分任务」误弹（需求 51） */
  artworkId?: number;
  /** 快照所属画板 ID（0913）：artworkId 会随画板保存推进（12666→12672 实锤），相等判据会误杀在途任务，
   * 恢复过滤/转交校验一律优先用 boardId（画板主键不漂移）；旧快照无此字段时按放行处理 */
  boardId?: number;
  /** 快照所属用户 ID：同浏览器换账号登录后旧快照不参与自动恢复（QA 9-7-2 退出重登错乱） */
  userId?: number;
  canvasSize?: { width: number; height: number };
  timestamp: number;
}

const SNAPSHOT_KEY = 'game-ui-splitter:resume:active';
/** 编码期快照的生命周期：编码只持续数秒，超时未推进（刷新后未再进入流程）即视为废弃 */
const ENCODING_SNAPSHOT_TTL_MS = 30 * 60 * 1000;
/** prepared/splitting 快照生命周期（0913 排查 F-07）：拆分为分钟级任务，超 24h 未推进的快照
 * 必然对应已终态/不可恢复会话，保留只会每次进画板空转 RESUME。timestamp 随阶段推进刷新，按最后活跃计 */
const RESUMABLE_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

const VALID_PHASES = ['encoding', 'prepared', 'splitting'] as const;

/** 快照条目的存储键：clientId 为空（编码期）时用合成键暂存 */
function snapshotKey(snap: SplitSessionSnapshot): string {
  return snap.clientId || `encoding:${snap.timestamp}`;
}

function parseOne(parsed: unknown): SplitSessionSnapshot | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as SplitSessionSnapshot;
  if (
    typeof p.clientId !== 'string' ||
    typeof p.sourceImageUrl !== 'string' ||
    !(VALID_PHASES as readonly string[]).includes(p.phase)
  ) {
    return null;
  }
  return p;
}

/** 序列化整个多槽存储（供测试） */
export function serializeSnapshotStore(sessions: Record<string, SplitSessionSnapshot>): string {
  return JSON.stringify({ v: 1, sessions });
}

/** 反序列化多槽存储，兼容迁移旧单槽对象（以其 clientId/合成键为条目键） */
export function parseSnapshotStore(raw: string | null): Record<string, SplitSessionSnapshot> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.sessions && typeof parsed.sessions === 'object') {
      const out: Record<string, SplitSessionSnapshot> = {};
      for (const snap of Object.values(parsed.sessions)) {
        const valid = parseOne(snap);
        if (valid) out[snapshotKey(valid)] = valid;
      }
      return out;
    }
    // 旧单槽格式：单个快照对象直接迁移
    const legacy = parseOne(parsed);
    if (legacy) return { [snapshotKey(legacy)]: legacy };
    return {};
  } catch {
    return {};
  }
}

/** 序列化快照（旧单槽兼容导出：单条包装为多槽存储） */
export function serializeSnapshot(snapshot: SplitSessionSnapshot): string {
  return serializeSnapshotStore({ [snapshotKey(snapshot)]: snapshot });
}

/** 反序列化快照（旧单槽语义兼容：取最新一条；非法输入返回 null，供测试） */
export function parseSnapshot(raw: string | null): SplitSessionSnapshot | null {
  const list = Object.values(parseSnapshotStore(raw)).sort((a, b) => b.timestamp - a.timestamp);
  return list[0] ?? null;
}

/** 去掉 OSS 处理参数，得到可比较的源图 URL（供测试） */
export function toSnapshotSourceUrl(url: string): string {
  const idx = url.indexOf('?x-oss-process=');
  return idx >= 0 ? url.slice(0, idx) : url;
}

/** 判断快照源图与当前选中图是否同一张（忽略 OSS 处理参数差异；供测试） */
export function isSameSourceImage(snapshotSourceUrl: string, selectedUrl: string): boolean {
  if (!snapshotSourceUrl || !selectedUrl) return false;
  return toSnapshotSourceUrl(snapshotSourceUrl) === toSnapshotSourceUrl(selectedUrl);
}

function loadStore(): Record<string, SplitSessionSnapshot> {
  const store = parseSnapshotStore(localStorage.getItem(SNAPSHOT_KEY));
  // 顺手清理：编码期快照超时未推进（编码只持续数秒）视为废弃，不参与任何恢复判定
  const now = Date.now();
  for (const [key, snap] of Object.entries(store)) {
    const age = now - (snap.timestamp || 0);
    if (snap.phase === 'encoding' && age > ENCODING_SNAPSHOT_TTL_MS) {
      delete store[key];
    } else if (snap.phase !== 'encoding' && age > RESUMABLE_SNAPSHOT_TTL_MS) {
      delete store[key];
    }
  }
  return store;
}

function persistStore(store: Record<string, SplitSessionSnapshot>) {
  if (Object.keys(store).length === 0) {
    localStorage.removeItem(SNAPSHOT_KEY);
  } else {
    localStorage.setItem(SNAPSHOT_KEY, serializeSnapshotStore(store));
  }
}

/** 是否存在可恢复的活跃快照（splitting/prepared 任一会话），供页面加载后自动续上任务触发。
 * currentUserId 传入时校验快照归属：同浏览器换账号登录后旧账号快照不参与自动恢复
 * （旧快照无 userId 字段则放行，兼容存量会话） */
export function hasActiveResumableSnapshot(currentUserId?: number): boolean {
  return readSnapshotStoreSnapshots(currentUserId).length > 0;
}

/** 读取全部可恢复快照（splitting/prepared，时间倒序），归属校验同 hasActiveResumableSnapshot */
export function readSnapshotStoreSnapshots(currentUserId?: number): SplitSessionSnapshot[] {
  return Object.values(loadStore())
    .sort((a, b) => b.timestamp - a.timestamp)
    .filter((snap) => {
      if (snap.phase !== 'splitting' && snap.phase !== 'prepared') return false;
      if (
        typeof currentUserId === 'number' &&
        currentUserId > 0 &&
        typeof snap.userId === 'number' &&
        snap.userId > 0 &&
        snap.userId !== currentUserId
      ) {
        return false;
      }
      return true;
    });
}

/** 物理清除全部活跃快照（登出/跨标签页认证重置时调用，防换账号读到他人会话） */
/** 判断快照是否属于当前画板：双方都有 boardId 时精确匹配（画板主键不漂移）；
 * 任一方缺失（旧快照未写 boardId/画板未就绪）一律放行——不再用会漂移的 artworkId 误杀在途任务 */
export function isSnapshotForCurrentBoard(snap: SplitSessionSnapshot, currentBoardId?: number): boolean {
  if (snap.boardId && currentBoardId) return snap.boardId === currentBoardId;
  return true;
}

export function clearSplitSessionSnapshot() {
  localStorage.removeItem(SNAPSHOT_KEY);
}

/** 按 clientId 读取单条快照（组件外使用：编排器/桥接模块零 watcher 泄漏，替代 useSplitSessionSnapshot 实例） */
export function readSplitSessionSnapshotById(clientId: string): SplitSessionSnapshot | null {
  if (!clientId) return null;
  return loadStore()[clientId] ?? null;
}

/** 按 clientId 物理清除单会话快照（恢复编排器终态收尾用：终态会话不再参与后续刷新恢复） */
export function clearSplitSessionSnapshotById(clientId: string) {
  if (!clientId) return;
  const store = loadStore();
  if (store[clientId]) {
    delete store[clientId];
    persistStore(store);
  }
}

export function useSplitSessionSnapshot() {
  const snapshot = ref<SplitSessionSnapshot | null>(null);

  /** 读取最新一条可恢复快照（splitting/prepared 优先；交互恢复用，并行场景取最近发起的会话） */
  function readSnapshot(): SplitSessionSnapshot | null {
    const resumable = readSnapshotStoreSnapshots();
    if (resumable.length > 0) return resumable[0];
    // 无 prepared/splitting 快照时回退最新编码期条目（编码期自动续用）
    return Object.values(loadStore()).sort((a, b) => b.timestamp - a.timestamp)[0] ?? null;
  }

  /** 读取全部活跃快照（时间倒序，含编码期；多会话恢复编排用） */
  function readSnapshots(): SplitSessionSnapshot[] {
    return Object.values(loadStore()).sort((a, b) => b.timestamp - a.timestamp);
  }

  /** 按 clientId 读取快照（多会话恢复定位用）；clientId 为空返回 null */
  function readSnapshotByClient(clientId: string): SplitSessionSnapshot | null {
    if (!clientId) return null;
    return loadStore()[clientId] ?? null;
  }

  /** 写入/覆盖快照：按 clientId upsert（并行任务互不覆盖）。
   * 拿到真实 clientId 落盘时，顺带清掉同源图的空 clientId 编码期条目（同一任务的暂存态） */
  function saveSnapshot(next: SplitSessionSnapshot) {
    snapshot.value = next;
    const store = loadStore();
    if (next.clientId) {
      for (const [key, snap] of Object.entries(store)) {
        if (!snap.clientId && isSameSourceImage(snap.sourceImageUrl, next.sourceImageUrl)) {
          delete store[key];
        }
      }
    }
    store[snapshotKey(next)] = next;
    persistStore(store);
  }

  /** 局部更新快照（如阶段推进），不允许覆盖 clientId / sourceImageUrl。
   * clientId 传入时精确更新该会话（并行必传）；缺省更新最新一条（单任务流既有语义） */
  function updateSnapshot(patch: Partial<Omit<SplitSessionSnapshot, 'clientId' | 'sourceImageUrl'>>, clientId?: string) {
    const store = loadStore();
    const target = (clientId ? store[clientId] : null)
      ?? Object.values(store).sort((a, b) => b.timestamp - a.timestamp)[0];
    if (!target) return;
    const merged: SplitSessionSnapshot = { ...target, ...patch, timestamp: Date.now() };
    store[snapshotKey(target)] = merged;
    persistStore(store);
    snapshot.value = merged;
  }

  /** 清除快照：clientId 传入只清该会话（并行互不影响）；缺省清全部（登出/认证重置等全局语义） */
  function clearSnapshot(clientId?: string) {
    if (clientId) {
      const store = loadStore();
      if (store[clientId]) delete store[clientId];
      persistStore(store);
      snapshot.value = null;
      return;
    }
    snapshot.value = null;
    localStorage.removeItem(SNAPSHOT_KEY);
  }

  /** 按源图清快照：编码期条目尚无 clientId（合成键）时按同源图定位清除（放弃流程/取消场景） */
  function clearSnapshotBySource(sourceImageUrl: string) {
    if (!sourceImageUrl) return;
    const store = loadStore();
    let changed = false;
    for (const [key, snap] of Object.entries(store)) {
      if (!snap.clientId && isSameSourceImage(snap.sourceImageUrl, sourceImageUrl)) {
        delete store[key];
        changed = true;
      }
    }
    if (changed) persistStore(store);
  }

  /** 同步内存态与 localStorage（供薄壳 onMounted 加载） */
  function hydrate() {
    snapshot.value = readSnapshot();
  }

  // 内存态变化时同步 localStorage（薄壳内 save/update/clear 已直接写盘，此 watch 兜底）
  watch(snapshot, (val) => {
    if (val) {
      const store = loadStore();
      store[snapshotKey(val)] = val;
      persistStore(store);
    }
  });

  return { snapshot, readSnapshot, readSnapshots, readSnapshotByClient, saveSnapshot, updateSnapshot, clearSnapshot, clearSnapshotBySource, hydrate };
}
